import _ from 'lodash'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { skipToken } from '@reduxjs/toolkit/query'

import { IconButton, Paper, Stack, Typography } from '@mui/material'
import ArrowDropDown from '@mui/icons-material/ArrowDropDown'
import ArrowDropUp from '@mui/icons-material/ArrowDropUp'

import { useGetNetworkMembersQuery, useGetNetworkQuery, useReplyNetworkJoinRequestMutation } from '@/core/services/network'

import NotFoundPage from '@/pages/error/NotFoundPage'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { getApiErrorMessage } from '@/utils/errors'

import MemberRequests from '../components/MemberRequests'
import PageContainer from '@/components/ui/containers/PageContainer'

import MembersTable from '../components/MembersTable'
import { NetworkMemberAccessLevel } from '@/core/models/network.model'

export default function MembersContainer() {
  const { networkId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)

  const { data: network } = useGetNetworkQuery(networkId ?? '', { skip: !networkId })
  const [replyNetworkJoinRequest, { isLoading: isReplying, originalArgs: replyNetworkJoinRequestArgs }] = useReplyNetworkJoinRequestMutation()
  const { data: networkMembers } = useGetNetworkMembersQuery(networkId ?? skipToken, { refetchOnMountOrArgChange: true })
  const [isVisible, setIsVisible] = useState(false)

  const membersData = useMemo(
    () =>
      networkMembers?.map(({ accessLevel, invitedBy, member }) => ({
        id: member._id,
        canLeave: true,
        canRemove: member._id !== network?.createdBy._id && authUser?._id === network?.createdBy._id,
        canPromote: member._id !== network?.createdBy._id && authUser?._id === network?.createdBy._id,
        email: member.email,
        establishment: member.establishment?.name,
        invitedBy: invitedBy ? `${invitedBy.userInfo.name} ${invitedBy.userInfo.surname}` : '',
        name: `${member.userInfo.name} ${member.userInfo.surname}`,
        role: accessLevel.toLowerCase(),
        isAdmin: accessLevel === NetworkMemberAccessLevel.ADMIN,
      })) ?? [],
    [authUser, network, networkMembers]
  )
  const { action, applicant } = replyNetworkJoinRequestArgs ?? {}

  const pendingMembers = useMemo(
    () =>
      _.map(network?.pendingMembers, (pendingMember) => ({
        id: pendingMember._id,
        isAccepting: isReplying && action === 'accept' && applicant === pendingMember._id,
        isDeclining: isReplying && action === 'decline' && applicant === pendingMember._id,
        fullName: `${pendingMember.userInfo.name} ${pendingMember.userInfo.surname}`,
      })),
    [action, applicant, isReplying, network]
  )

  if (!network) return null

  if (network.createdBy._id !== authUser?._id) {
    return (
      <Stack alignItems="center" justifyContent="center" width="100%" height="100%">
        <NotFoundPage />
      </Stack>
    )
  }

  return (
    <>
      <PageContainer>
        <Paper square sx={{ position: 'sticky', top: 0 }} >
          <Stack px={2} pt={2} direction="row" justifyContent={"space-between"} alignItems={"center"} pb={2}>
            <Typography component="h2" variant="h2"> {t('page.network.tab.members.label')} </Typography>
            <IconButton onClick={() => setIsVisible(!isVisible)}>
              {isVisible ? <ArrowDropDown /> : <ArrowDropUp />}
            </IconButton>
          </Stack>
          {isVisible && <MembersTable data={membersData} />}
        </Paper>
      </PageContainer>
      <MemberRequests
        onAccept={async (memberId) => {
          if (!networkId) return
          try {
            await replyNetworkJoinRequest({ networkId, action: 'accept', applicant: memberId }).unwrap()
          } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, t('notification.network.acceptJoinRequest.error')), { variant: 'error' })
          }
        }}
        pendingMembers={pendingMembers}
      />
    </>
  )
}
